import type {Dispatch, ThunkAction} from 'src/reducers';
import type {ConfigData, Layout} from 'src/types/job-widget';
import * as reduxApi from 'src/utils/redux-api-bff';
import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import {showAndCaptureApiError} from 'src/action-creators/modal';


export const RECEIVE_CONFIG_DATA = 'config-data/receive';
export const UPDATE_CONFIG_DATA = 'config-data/update';
export const RESET_DATA = 'data/reset';
export const REMOVE_CONFIG_DATA_FILTER_INCLUDED_ID =
  'config-data/remove-filter-includedId';
export const UPDATE_CONFIG_DATA_ATTRIBUTES = 'config-data/update-attributes';
export const UPDATE_LAYOUT = 'layout/update';

type ReceiveConfigData = {
  type: 'config-data/receive';
  payload: {
    configData: ConfigData;
  };
};

type UpdateConfigData = {
  type: 'config-data/update';
  payload: {
    configData: ConfigData;
  };
};

type UpdateLayout = {
  type: 'layout/update';
  payload: Layout;
};

type RemoveConfigDataFilterIncludedId = {
  type: 'config-data/remove-filter-includedId';
  payload: {
    id: string;
  };
};

type UpdateConfigDataAttributes = {
  type: 'config-data/update-attributes';
  payload: {
    attribute: string;
  };
};

type ResetData = {
  type: 'data/reset';
};

export type JobListActions =
  | ReceiveConfigData
  | UpdateConfigData
  | UpdateLayout
  | ResetData
  | RemoveConfigDataFilterIncludedId
  | UpdateConfigDataAttributes;

const receiveConfigData = (payload: {
  configData: ConfigData;
}): ReceiveConfigData => ({
  type: 'config-data/receive',
  payload,
});

const updateData = (payload: {configData: ConfigData}): UpdateConfigData => ({
  type: 'config-data/update',
  payload,
});

export const updateLayout = (payload: Layout): UpdateLayout => ({
  type: 'layout/update',
  payload,
});

const removeDataFilterIncludedId = (payload: {
  id: string;
}): RemoveConfigDataFilterIncludedId => ({
  type: 'config-data/remove-filter-includedId',
  payload,
});

const updateDataAttributes = (payload: {
  attribute: string;
}): UpdateConfigDataAttributes => ({
  type: 'config-data/update-attributes',
  payload,
});

const resetData = (): ResetData => ({
  type: 'data/reset',
});

export const getConfigData: (pageId: string) => ThunkAction<unknown> =
  (pageId) => (dispatch: Dispatch) =>
    dispatch(reduxApi.get(`landing-pages/job-widget/config/${pageId}`))
      .then((payload) => {
        dispatch(
          receiveConfigData({
            configData: payload ?? null,
          }),
        );
      })
      .catch((err) =>
        dispatch(showAndCaptureApiError(err, {text: 'Job widget api error'})),
      );

export const updateConfigData: (newData: ConfigData) => ThunkAction<unknown> =
  (newData) => (dispatch: Dispatch) =>
    dispatch(reduxApi.post(`landing-pages/job-widget/config`, newData)).then(
      () => {
        dispatch(updateData({configData: newData ?? null}));
      },
    );

export const resetConfigData =
  (): ((dispatch: Dispatch) => void) => (dispatch: Dispatch) => {
    dispatch(resetData());
  };

export const removeConfigDataFilterIncludedId: (
  id: string,
) => ThunkAction<unknown> = (id) => (dispatch: Dispatch) =>
  dispatch(removeDataFilterIncludedId({id}));

export const updateConfigDataAttributes: (
  attribute: string,
) => ThunkAction<unknown> = (attribute) => (dispatch: Dispatch) =>
  dispatch(updateDataAttributes({attribute}));

export const getSiteChatbots: (siteId: string) => ThunkAction<unknown> = flow(
  key((siteId) => `siteChatbots-${siteId}`),
  fetching(),
)(
  (siteId) => (dispatch: Dispatch) =>
    dispatch(reduxApi.get(`landing-pages/career-site/${siteId}/chatbot`))
      .then((response) => {
        return response;
      })
      .catch((err) => console.error(err)),
);

export const updateChatbotForPage: (
  pageId: string,
  chatbotId: string,
) => ThunkAction<unknown> = (pageId, chatbotId) => (dispatch: Dispatch) =>
  dispatch(reduxApi.put(`landing-pages/page/${pageId}/chatbot/${chatbotId}`))
    .then((response) => {
      return response;
    })
    .catch((err) => console.error(err));

export const deleteChatbotForPage: (pageId: string) => ThunkAction<unknown> =
  (pageId) => (dispatch: Dispatch) =>
    dispatch(reduxApi.del(`landing-pages/page/${pageId}/chatbot`))
      .then((response) => {
        return response;
      })
      .catch((err) => console.error(err));

export const getChatbotForPage: (pageId: string) => ThunkAction<unknown> = flow(
  key((pageId) => `chatbot-${pageId}`),
  fetching(),
)(
  (pageId) => (dispatch: Dispatch) =>
    dispatch(reduxApi.get(`landing-pages/page/${pageId}/chatbot`))
      .then((response) => {
        return response;
      })
      .catch((err) => console.error(err)),
);
